/**
 * Central author/publisher profile — the single source of truth for who
 * runs this site. Referenced by /about, /contact, /masthead, /authors/*,
 * byline chips, JSON-LD Person schemas, and email disclosures.
 *
 * To customize: swap the values below. Every trust page picks up the
 * change automatically. To add a real photo, drop a 400x400 JPG at
 * /app/frontend/public/authors/jay-mursch.jpg and set `photoUrl` to
 * `/authors/jay-mursch.jpg`.
 */

export const AUTHOR = {
  slug: 'jay-mursch',
  name: 'Jay Mursch',
  role: 'Founder & Editor',
  location: 'Cleveland, OH',
  email: 'jamursch@gmail.com',
  // Photo — real if the file exists at /public/authors/jay-mursch.jpg,
  // otherwise the AuthorAvatar component renders initials.
  photoUrl: '/authors/jay-mursch.jpg',
  // Public profiles (fill in whichever you have)
  linkedin: 'https://www.linkedin.com/in/jaymursch/',
  x: '',
  github: '',
  // Short one-liner for byline chips
  headline: 'Builds travel + data products. Fly ~40 segments a year.',
  // Full bio — used on /about and /authors/jay-mursch
  bio: [
    "I've been flying 30-50 segments a year for the last decade — mostly out of Cleveland and Chicago, with regular trips through ATL, DFW, and JFK. That much time in terminals turns you into an amateur crowd-pattern watcher. You start to notice that the same airport at 6 PM on Sunday is a completely different building than at 6 PM on Tuesday, and the airline apps never explain why.",
    "I started How Busy Is My Airport to answer one question I kept asking myself in the security line: was there a better time to be here? The site pulls live flight schedules from FlightAware, weights them by aircraft size and direction, and outputs a plain-English crowd score for any airport at any time. That's it — no signup, no ads-first design, no upsells.",
    "My background is in product and analytics — I've spent 15+ years building tools that turn messy operational data into decisions people can actually make. This site is that discipline pointed at a problem I have personally.",
  ],
  credentials: [
    '15+ years building data products (product & analytics)',
    'Cleveland Creative Group — founder',
    'Frequent flyer: 30-50 segments/yr across US majors',
  ],
  // Site-wide contact meta
  responseTimePromise: 'within 3 business days',
  timezone: 'America/New_York',
};

/**
 * Site-wide organization identity (mirrors and extends the JSON-LD Org).
 */
export const ORG = {
  name: 'How Busy Is My Airport',
  legalOwner: 'Cleveland Creative Group',
  domain: 'https://howbusyismyairport.com',
  founded: '2025',
  contactEmail: 'jamursch@gmail.com',
  // Mailing address for AdSense compliance. Update if using a real P.O. Box.
  mailingAddress: {
    line1: 'Cleveland Creative Group',
    line2: 'c/o Jay Mursch',
    city: 'Cleveland',
    region: 'OH',
    country: 'USA',
  },
};

/** JSON-LD Person schema for author byline pages */
export function personLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: AUTHOR.name,
    jobTitle: AUTHOR.role,
    url: `${ORG.domain}/authors/${AUTHOR.slug}`,
    image: `${ORG.domain}${AUTHOR.photoUrl}`,
    email: `mailto:${AUTHOR.email}`,
    sameAs: [AUTHOR.linkedin, AUTHOR.x, AUTHOR.github].filter(Boolean),
    worksFor: {
      '@type': 'Organization',
      name: ORG.name,
      url: ORG.domain,
    },
  };
}
